/**
 * Progress - 진행률 표시 컴포넌트
 *
 * @param variant - 'bar' (기본 막대) | 'circle' (원형 SVG) | 'steps' (단계 도트)
 * @param value / max - 현재 값과 최대 값
 * @param showLabel - true이면 퍼센트 또는 커스텀 라벨 표시
 */
'use client';

import React from 'react';
import styles from './Progress.module.css';

export interface ProgressProps {
  value: number;
  max?: number;
  variant?: 'bar' | 'circle' | 'steps';
  size?: 'sm' | 'md' | 'lg';
  thickness?: 'thin' | 'medium' | 'thick';
  color?: string;
  showLabel?: boolean;
  label?: string;
  totalSteps?: number;
  className?: string;
}

const THICKNESS_MAP: Record<string, number> = { thin: 4, medium: 8, thick: 12 };
const CIRCLE_SIZES: Record<string, number> = { sm: 32, md: 48, lg: 64 };
const CIRCLE_STROKES: Record<string, number> = { sm: 3, md: 4, lg: 5 };
const CIRCLE_FONT: Record<string, string> = {
  sm: 'var(--font-size-2xs)',
  md: 'var(--font-size-xs)',
  lg: 'var(--font-size-sm)',
};

export default function Progress({
  value,
  max = 100,
  variant = 'bar',
  size = 'md',
  thickness = 'medium',
  color,
  showLabel = false,
  label,
  totalSteps = 5,
  className = '',
}: ProgressProps) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const displayLabel = label ?? `${Math.round(pct)}%`;
  const fillColor = color || 'var(--color-brand-50)';

  // Steps variant: horizontal dots showing progress
  if (variant === 'steps') {
    const activeSteps = Math.round((pct / 100) * totalSteps);
    return (
      <div
        className={[styles.stepsContainer, className].filter(Boolean).join(' ')}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={displayLabel}
      >
        <div className={styles.stepsTrack}>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span
              key={i}
              className={`${styles.step} ${i < activeSteps ? styles.stepActive : ''}`}
              style={i < activeSteps ? { background: fillColor } : undefined}
            />
          ))}
        </div>
        {showLabel && <span className={styles.barLabel}>{displayLabel}</span>}
      </div>
    );
  }

  // Circle variant
  if (variant === 'circle') {
    const svgSize = CIRCLE_SIZES[size];
    const stroke = CIRCLE_STROKES[size];
    const radius = (svgSize - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;

    return (
      <div
        className={[styles.circleContainer, className].filter(Boolean).join(' ')}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={displayLabel}
      >
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          className={styles.circleSvg}
        >
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke="var(--color-gray-10)"
            strokeWidth={stroke}
          />
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke={fillColor}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={styles.circleProgress}
          />
        </svg>
        {showLabel && (
          <span className={styles.circleLabel} style={{ fontSize: CIRCLE_FONT[size] }}>
            {displayLabel}
          </span>
        )}
      </div>
    );
  }

  // Bar variant (default)
  const barHeight = THICKNESS_MAP[thickness];

  return (
    <div
      className={[styles.barContainer, className].filter(Boolean).join(' ')}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={displayLabel}
    >
      <div className={styles.barContent}>
        <div className={styles.track} style={{ height: barHeight }}>
          <div className={styles.fill} style={{ width: `${pct}%`, background: fillColor }} />
        </div>
        {showLabel && <span className={styles.barLabel}>{displayLabel}</span>}
      </div>
    </div>
  );
}
